import { css } from '@emotion/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import PermissionedLink from '$/components/Router/PermissionedLink';
import SessionsDropdown from '$/components/ScheduleDropdownInput';
import Form from '$/components/ui/Form';
import { PageMode } from '$/components/ui/Input/PageBuilder.types';
import { EwebinarSessions, Session, SessionStatus } from '$/GraphQL/schema/schema';
import { WidgetEventType } from '$/widget/src/interface';

import { usePageEditorContext } from '../PageEditorContext';
import { RegFormSettingsForForm } from './types';
import { Popup } from '$/components/ui/Popup';

interface SessionFieldProps {
  isWidget?: boolean;
  sessions?: EwebinarSessions;
}

export const SessionField: React.FC<SessionFieldProps> = ({ sessions, isWidget }) => {
  const { setValue, watch } = useFormContext<RegFormSettingsForForm & { [k: string]: any }>();
  const { ewebinar, hideBranding, language, mode, teamId, themeSettings, series } =
    usePageEditorContext();

  React.useEffect(() => {
    // Auto select if there is only one session
    if (mode === PageMode.edit) return;
    if (sessions?.sessions.length === 1 && sessions?.sessions[0].status !== SessionStatus.Full) {
      setTimeout(() => setValue('session', sessions.sessions[0], { shouldDirty: true }), 100);
    }
  }, [sessions?.sessions, mode, setValue]);

  const session: Session | undefined = watch('session');
  React.useEffect(() => {
    if (isWidget) {
      window?.xprops?.emit({
        event: WidgetEventType.UpdateSession,
        session: session?.value,
      });
    }
  }, [isWidget, session]);

  return (
    <>
      <Form.Field
        name='session'
        className='reg-field'
        component={
          <SessionsDropdown
            css={css`
              font-size: 1.1rem;
              .menu > .item {
                font-size: 1.1rem !important;
              }
            `}
            language={language}
            sessions={sessions}
            isRegister={mode === PageMode.edit}
            primaryColor={themeSettings.primaryColor}
            hideBranding={Boolean(hideBranding)}
            teamId={teamId}
            showPlaceholder={Boolean(series)}
            placeholder={series ? 'Select a session to join' : undefined}
          />
        }
      />
      {mode === PageMode.edit && ewebinar?.set.id && (
        <PermissionedLink
          className='absolute bg-white px-2 font-body text-color-brand font-semibold'
          css={css`
            top: 1.75rem;
            right: 3.5rem;
            text-decoration: none !important;
          `}
          to={`/home/webinar/${ewebinar?.set.id}/edit/schedule`}
        >
          Edit schedule
        </PermissionedLink>
      )}
      {mode === PageMode.edit && series && (
        <Popup
          className='absolute bg-white px-2 font-body text-color-brand font-semibold'
          content='The available sessions in this drop-down will come from the schedule of the webinar being registered for.'
          css={css`
            top: 1.75rem;
            right: 1.5rem;
          `}
        />
      )}
    </>
  );
};
